/**
 * VoiceRecorder Component
 * Registrazione messaggi vocali
 */

import { memo, useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Send, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { iconTip } from '@/components/messages/ChatTooltip';

/** Messaggio chiaro quando il browser non da' il microfono. */
export function microphoneErrorMessage(err: unknown): string {
  const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Il microfono e\' bloccato. Consenti l\'accesso dal lucchetto accanto all\'indirizzo del sito, poi riprova.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'Nessun microfono trovato: collegane uno e riprova.';
  if (name === 'NotReadableError') return 'Il microfono e\' gia\' in uso da un\'altra applicazione (es. una videochiamata). Chiudila e riprova.';
  return 'Non riesco ad avviare la registrazione. Riprova o controlla le impostazioni del microfono.';
}

/**
 * Microfono mono: Meta accetta i vocali solo a un canale. Se il browser rifiuta il
 * vincolo (OverconstrainedError/TypeError) si riprova con il microfono normale.
 */
export async function getMonoMicrophone(media: Pick<MediaDevices, 'getUserMedia'>): Promise<MediaStream> {
  try {
    return await media.getUserMedia({ audio: { channelCount: 1 } });
  } catch (err) {
    const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : '';
    if (name === 'OverconstrainedError' || name === 'TypeError' || err instanceof TypeError) {
      return media.getUserMedia({ audio: true });
    }
    throw err;
  }
}

interface VoiceRecorderProps {
  onSend: (audio: Blob) => void;
  onCancel: () => void;
  isRecording: boolean;
  onStartRecording: () => void;
  /** Vocale non inviabile ora (es. finestra di 24 ore chiusa sul numero Ufficiale). */
  disabledReason?: string;
  /** Click sul microfono quando disabledReason e' impostato (es. apre i modelli approvati). */
  onBlockedClick?: () => void;
}

export const VoiceRecorder = memo(function VoiceRecorder({
  onSend,
  onCancel,
  isRecording,
  onStartRecording,
  disabledReason,
  onBlockedClick,
}: VoiceRecorderProps) {
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = useCallback(async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Questo browser non permette di registrare vocali.');
        return;
      }
      const stream = await getMonoMicrophone(navigator.mediaDevices);
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Tipo reale della registrazione (Chrome registra WebM, Safari MP4): prima veniva
        // dichiarato sempre OGG e WAHA riceveva un WebM con l'etichetta sbagliata.
        const realType = mediaRecorder.mimeType || chunksRef.current[0]?.type || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: realType });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setDuration(0);
      timerRef.current = setInterval(() => {
        setDuration((d) => d + 1);
      }, 1000);
      onStartRecording();
    } catch (err) {
      console.error('Error starting recording:', err);
      toast.error(microphoneErrorMessage(err), { duration: 6000 });
    }
  }, [onStartRecording]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleSend = useCallback(() => {
    if (audioBlob) {
      onSend(audioBlob);
      setAudioBlob(null);
      setDuration(0);
    }
  }, [audioBlob, onSend]);

  const handleCancel = useCallback(() => {
    stopRecording();
    setAudioBlob(null);
    setDuration(0);
    onCancel();
  }, [stopRecording, onCancel]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isRecording && !audioBlob) {
    if (disabledReason) {
      return (
        <button
          type="button"
          onClick={onBlockedClick}
          aria-disabled="true"
          style={{ width: '42px', height: '42px', display: 'grid', placeItems: 'center' }}
          className="rounded-full opacity-40 cursor-not-allowed"
          {...iconTip(disabledReason)}
        >
          <Mic className="w-5 h-5 text-[#787774]" />
        </button>
      );
    }
    return (
      <button
        onClick={startRecording}
        style={{ width: '42px', height: '42px', display: 'grid', placeItems: 'center' }}
        className="rounded-full hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
        {...iconTip('Registra un messaggio vocale')}
      >
        <Mic className="w-5 h-5 text-[#787774]" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-red-50 dark:bg-red-900/20 rounded-full">
      <button
        onClick={handleCancel}
        className="p-2 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        {...iconTip('Elimina la registrazione')}
      >
        <Trash2 className="w-5 h-5 text-red-500" />
      </button>

      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-sm font-medium text-red-500">
          {formatDuration(duration)}
        </span>
      </div>

      {isRecording ? (
        <button
          onClick={stopRecording}
          className="p-2 rounded-full bg-red-500 hover:bg-red-600 transition-colors"
          {...iconTip('Ferma la registrazione')}
        >
          <Square className="w-4 h-4 text-white" />
        </button>
      ) : (
        <button
          onClick={handleSend}
          className="p-2 rounded-full bg-[#2383E2] hover:bg-[#1a6fc2] transition-colors"
          {...iconTip('Invia il vocale')}
        >
          <Send className="w-4 h-4 text-white" />
        </button>
      )}
    </div>
  );
});
